/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { Globe2, Gauge, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import Seo from "@/components/Seo";
import { PageShell, SectionWrapper } from "./WorldwideProjects.page-primitives";
import DotGrid from "../components/customCopmonent/DotGrid";
import SpecialProjectsPage from "../components/SpecialProjectsPage";
import {
	PageHeaderHero,
	PAGE_HERO_CLASS,
} from "../components/customCopmonent/Text/PageHeaderHero";

const bgimage = "/images/header/WorldWideProjects.JPG";

const WorldwideProjects = () => {
	const { t } = useTranslation();

	const L = (key: string) => t(`worldwidePage.labels.${key}`);

	const specialProjectsData = {
		title: t("worldwidePage.title"),
		description: t("worldwidePage.description"),
		meta: t("worldwidePage.meta", { returnObjects: true }) as string[],
		projects: [
			{
				id: "3",
				title: "Lowa Fertilizer Company",
				application: t(
					"worldwidePage.appDescriptions.fertilizerProcessingHeatExchanger",
				),
				capacity: "1200 kW",
				image: "/images/worldwide/LowaFertilizerCompanyG.jpg",
				details: {
					[L("certification")]: "ASME U Stamp + NB Stamp",
					[L("capacity")]: "1200 kW",
					[L("installationLocation")]: "United States of America",
					[L("country")]: "United States of America",
					[L("shellDiameter")]: "22 Inch",
					[L("overallLength")]: "5.6 Mtr",
					[L("tubeMoc")]: "SA 249 TP 304L",
					[L("shellMoc")]: "SA 240 TP 304L",
					[L("weight")]: "5500 Kg",
				},
				aplication: [
					t("worldwidePage.applications.chemicalProcessing"),
					t("worldwidePage.applications.industrialHeating"),
				],
			},
			{
				id: "4",
				title: "NTPC-Vijayawada & Krishnapattanam",
				application: t(
					"worldwidePage.appDescriptions.boilerFeedWaterHeatExchanger",
				),
				capacity: "2000 kW",
				image: "/images/worldwide/ntp.jpeg",
				details: {
					[L("country")]: "India",
					[L("capacity")]: "2000 kW",
					[L("shellDiameter")]: "38 Inch",
					[L("overallHeight")]: "4.2 Mtr",
					[L("tubeMoc")]: "SA 249 TP 304",
					[L("shellMoc")]: "SA 516 Gr. 70",
					[L("construction")]: "TEMA C - BFW",
					[L("weight")]: "15000 Kg",
				},
				aplication: [
					t("worldwidePage.applications.powerGeneration"),
					t("worldwidePage.applications.thermalSystems"),
				],
			},
			{
				id: "5",
				title: "NTPC-Khargone",
				application: t(
					"worldwidePage.appDescriptions.integratedHeatExchangers",
				),
				capacity: "300 kW & 2400 kW",
				image: "/images/worldwide/DoosanScodaPowerIndiaG.jpg",
				details: {
					[L("country")]: "India",
					[L("applicableIndustry")]: t("worldwidePage.values.power"),
					[L("project")]: "NTPC-Khargone",
					[L("specialFeature")]: t(
						"worldwidePage.appDescriptions.integratedHeatExchangers",
					),
					[L("capacity")]: "300 kW & 2400 kW",
					[L("shellMoc")]: "SA 106 Gr. B",
					[L("tubeMoc")]: "SA 213 TP 304",
					[L("temaType")]: "BEW",
				},
				aplication: [
					t("worldwidePage.applications.powerGeneration"),
					t("worldwidePage.applications.thermalSystems"),
				],
			},
			{
				id: "19",
				title: "ABB Limited",
				application: t("worldwidePage.appDescriptions.abblimitedShellTube"),
				capacity: "200 kW",
				image: "/images/worldwide/abplimited.png",
				details: {
					[L("country")]: "India",
					[L("industry")]: t("worldwidePage.values.powerSector"),
					[L("capacity")]: "200 kW",
					[L("tube")]: "Aluminium",
					[L("fins")]: "Aluminium",
					[L("fanMake")]: "Zeihl Abegg (3 fans working + 1 Standby)",
					[L("tubesheet")]: "Carbon Steel",
					[L("other")]: "Carbon Steel",
				},
				aplication: [
					t("worldwidePage.applications.powerGeneration"),
					t("worldwidePage.applications.equipmentCooling"),
				],
			},
			{
				id: "18",
				title: "EIL - ONGC, Panipat",
				application: t("worldwidePage.appDescriptions.duplexShellTube"),
				capacity: "2800 kW",
				image: "/images/worldwide/ONGC.jpg",
				details: {
					[L("country")]: "India",
					[L("applicableIndustry")]: t("worldwidePage.values.oilGas"),
					[L("project")]: "EIL - ONGC, Panipat",
					[L("capacity")]: "2800 kW",
					[L("shellDiameter")]: "36 Inch",
					[L("overallLength")]: "7.4 meter",
					[L("shellMoc")]: "SA 516 Gr. 70",
					[L("tubeMoc")]: "SA 213 TP 316L",
					[L("temaType")]: "BEW",
					[L("specialFeature")]: t(
						"worldwidePage.appDescriptions.duplexShellTube",
					),
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.thermalSystems"),
				],
			},
			{
				id: "2",
				title: "CMPC Celulosa",
				application: t("worldwidePage.appDescriptions.lowPressureBfwHeater"),
				capacity: "20228 kW",
				image: "/images/worldwide/CMP.jpeg",
				details: {
					[L("certification")]: "CE-PED with Module G",
					[L("capacity")]: "20228 kW",
					[L("installationLocation")]: "Chile, South America",
					[L("country")]: "Chile",
					[L("shellDiameter")]: "40 Inch",
					[L("overallLength")]: "6 Mtr",
					[L("steamFlow")]: "32481 kg/hr",
					[L("boilerFeedWaterFlow")]: "446000 kg/hr",
					[L("temaClass")]: "NEN",
					[L("emptyWeight")]: "10000 Kg",
				},
				aplication: [
					t("worldwidePage.applications.industrialHeating"),
					t("worldwidePage.applications.processIndustry"),
				],
			},
			{
				id: "8",
				title: "IOCL Gas Pipeline Project",
				application: t("worldwidePage.appDescriptions.ioclgaspiplinne"),
				capacity: "500 kW",
				image: "/images/worldwide/IOCL.jpg",
				details: {
					[L("country")]: "India",
					[L("capacity")]: "500 kW",
					[L("designStandard")]: "API 661",
					[L("core")]: t("worldwidePage.values.plateFinnedType"),
					[L("tube")]: "Carbon Steel (Galvanised)",
					[L("other")]: "Carbon Steel",
					[L("fan")]: "GRP",
					[L("weight")]: "8000 Kg",
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.thermalSystems"),
				],
			},
			{
				id: "9",
				title: "CPRI, Bangalore",
				application: t("worldwidePage.appDescriptions.abblimitedShellTube"),
				capacity: "150 kW",
				image: "/images/worldwide/CPR.jpeg",
				details: {
					[L("country")]: "India",
					[L("capacity")]: "150 kW",
					[L("tube")]: "SS 304",
					[L("fins")]: "Aluminium",
					[L("fanMake")]: "Zeihl Abegg",
					[L("tubesheet")]: "Carbon Steel",
					[L("other")]: "Carbon Steel",
				},
				aplication: [
					t("worldwidePage.applications.electricalEquipmentCooling"),
					t("worldwidePage.applications.testingFacilities"),
				],
			},
			{
				id: "10",
				title: "Doosan Power",
				application: t("worldwidePage.appDescriptions.abblimitedShellTube"),
				capacity: "125 kW",
				image: "/images/worldwide/Doosan2Powe.jpg",
				details: {
					[L("country")]: "India",
					[L("capacity")]: "125 kW",
					[L("tube")]: "Aluminium",
					[L("fins")]: "Aluminium",
					[L("fanMake")]: "Zeihl Abegg (2W + 1S)",
					[L("tubesheet")]: "Carbon Steel",
					[L("other")]: "Carbon Steel",
				},
				aplication: [
					t("worldwidePage.applications.powerGeneration"),
					t("worldwidePage.applications.equipmentCooling"),
				],
			},
			{
				id: "6",
				title: "Bangalore V Valley Project",
				application: t("worldwidePage.appDescriptions.sludgeHeater"),
				capacity: "872.2 kW",
				image: "/images/worldwide/Banglore.jpeg",
				details: {
					[L("country")]: "India",
					[L("capacity")]: "872.2 kW",
					[L("outerPipe")]: "SS 316",
					[L("innerPipe")]: "SS 316",
					[L("other")]: "SS 316",
				},
				aplication: [
					t("worldwidePage.applications.waterTreatment"),
					t("worldwidePage.applications.wastewaterProcessing"),
				],
			},
			{
				id: "11",
				title: "Saudi Aramco - Jafurah",
				application: t("worldwidePage.appDescriptions.airCooledAfterCooler"),
				capacity: "",
				image: "/images/worldwide/MarjanDevelopmentProjectG.jpg",
				details: {
					[L("country")]: "Saudi",
					[L("industry")]: t("worldwidePage.values.oilGas"),
					[L("project")]: "Saudi Aramco - Jafurah",
					[L("designStandard")]: "ASME Sec VIII Div.1",
					[L("certification")]: "ASME U Stamp",
					[L("tube")]: "Stainless Steel 316L",
					[L("header")]: "Stainless Steel 316L",
					[L("structuralMaterial")]: "Carbon Steel",
					[L("painting")]: t("worldwidePage.values.nacePainting"),
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.airCoolingSystems"),
				],
			},
			{
				id: "12",
				title: "PDO Oman – Rabab Harwheel Integrated Project",
				application: t("worldwidePage.appDescriptions.pdoman"),
				capacity: "2 kW",
				image: "/images/worldwide/PDOoma.jpeg",
				details: {
					[L("country")]: "Oman",
					[L("capacity")]: "2 kW",
					[L("designStandard")]: "API 661",
					[L("tube")]: "Stainless Steel 316L",
					[L("header")]: "Stainless Steel 316L",
					[L("motor")]: "As per PDO Oman AVL",
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.airCoolingSystems"),
				],
			},
			{
				id: "13",
				title: "ADNOC - BAB Far North",
				application: t("worldwidePage.appDescriptions.airCooledCombiCooler"),
				capacity: "",
				image: "/images/worldwide/ADNO.jpeg",
				details: {
					[L("country")]: "United Arab Emirates",
					[L("capacity")]: "",
					[L("designStandard")]: "API 661, ADNOC Specs, ASME Sec VIII Div.1",
					[L("certification")]: "ASME U Stamp",
					[L("tube")]: "Stainless Steel 316L",
					[L("header")]: "Stainless Steel 316L",
					[L("structuralMaterial")]: "Carbon Steel",
					[L("painting")]: t("worldwidePage.values.nacePainting"),
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.thermalSystems"),
				],
			},
			{
				id: "14",
				title: "ADNOC – Hail & Gasha Offshore",
				application: t("worldwidePage.appDescriptions.airCooledCombiCooler"),
				capacity: "",
				image: "/images/worldwide/ADNOC-HAI.jpeg",
				details: {
					[L("country")]: "United Arab Emirates",
					[L("capacity")]: "",
					[L("designStandard")]: "ASME Sec VIII Div.1",
					[L("certification")]: "ASME U Stamp",
					[L("tube")]: "Stainless Steel 316L",
					[L("header")]: "Stainless Steel 316L",
					[L("structuralMaterial")]: "Carbon Steel",
					[L("painting")]: t("worldwidePage.values.nacePainting"),
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.offshoreSystems"),
				],
			},
			{
				id: "15",
				title: "Saudi Aramco - Zuluf",
				application: t("worldwidePage.appDescriptions.airCooledOilCooler"),
				capacity: "",
				image: "/images/worldwide/Zulu.jpeg",
				details: {
					[L("country")]: "Saudi Arabia",
					[L("capacity")]: "",
					[L("designStandard")]: "ASME Sec VIII Div.2",
					[L("certification")]: "ASME U2 Stamp",
					[L("tube")]: "Stainless Steel 316L",
					[L("header")]: "Stainless Steel 316L",
					[L("structuralMaterial")]: "Carbon Steel",
					[L("painting")]: "C5M",
				},
				aplication: [
					t("worldwidePage.applications.oilGasProcessing"),
					t("worldwidePage.applications.coolingSystems"),
				],
			},
			{
				id: "16",
				title: "Basrah Gas Company",
				application: t("worldwidePage.appDescriptions.compressedAirCooler"),
				capacity: "12.3 kW",
				image: "/images/worldwide/Basrah Gas Company, Iraq GG.png",
				details: {
					[L("country")]: "Iraq",
					[L("capacity")]: "12.3 kW",
					[L("tube")]: "SS 316",
					[L("fins")]: "Aluminium",
					[L("fanBlade")]: "GRP",
					[L("tubesheet")]: "SS 316",
					[L("other")]: "Carbon Steel",
				},
				aplication: [
					t("worldwidePage.applications.gasProcessing"),
					t("worldwidePage.applications.airCoolingSystems"),
				],
			},
			{
				id: "17",
				title: "Anchor Offshore - ONGC",
				application: t("worldwidePage.appDescriptions.seaWaterCooledAirCooler"),
				capacity: "5 kW",
				image: "/images/worldwide/Ancho.jpeg",
				details: {
					[L("country")]: "India",
					[L("capacity")]: "5 kW",
					[L("tube")]: "Cu Ni 90:10",
					[L("fins")]: "Aluminium",
					[L("fanBlade")]: "Aluminium",
					[L("tubesheet")]: "Naval Brass",
					[L("other")]: "SS 316 L",
				},
				aplication: [
					t("worldwidePage.applications.offshoreSystems"),
					t("worldwidePage.applications.marineCooling"),
				],
			},
			{
				id: "1",
				title: "JSW, Bellary",
				application: t("worldwidePage.appDescriptions.wasteHeatRecovery"),
				capacity: "",
				image: "/images/worldwide/REPUTER.png",
				details: {
					[L("country")]: "India",
					[L("industry")]: t("worldwidePage.values.steelSector"),
					[L("project")]: "JSW, Bellary",
					[L("application")]: t(
						"worldwidePage.appDescriptions.wasteHeatRecovery",
					),
					[L("designTemperature")]: "850 °C",
					[L("moc")]: "SS 310",
				},
				aplication: [
					t("worldwidePage.applications.steelMetallurgy"),
					t("worldwidePage.applications.wasteHeatRecovery"),
				],
			},
		],
		assurance: [
			{
				title: t("worldwidePage.assurance.globalReliability.title"),
				text: t("worldwidePage.assurance.globalReliability.text"),
				icon: Globe2,
			},
			{
				title: t("worldwidePage.assurance.documentationConfidence.title"),
				text: t("worldwidePage.assurance.documentationConfidence.text"),
				icon: ShieldCheck,
			},
			{
				title: t("worldwidePage.assurance.performanceFocus.title"),
				text: t("worldwidePage.assurance.performanceFocus.text"),
				icon: Gauge,
			},
		],
	};

	return (
		<PageShell>
			<Seo
				titleKey="worldwidePage.seo.title"
				descriptionKey="worldwidePage.seo.description"
				path="/worldwide-projects"
			/>
			<Navigation />{" "}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
						proximity={100}
						shockRadius={200}
						shockStrength={3}
						resistance={750}
						returnDuration={1.5}
					/>
				</div>
				<PageHeaderHero
					// align="center"
					title={t("worldwidePage.title")}
					description={t("worldwidePage.tagline")}
					meta={t("worldwidePage.meta", { returnObjects: true }) as string[]}
				/>
			</SectionWrapper>
			<SpecialProjectsPage data={specialProjectsData} />
			<FooterSection />
		</PageShell>
	);
};

export default WorldwideProjects;
